import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/apiClient";
import { Download, Edit3, Plus, Trash2, Upload } from "lucide-react";
import { Input, SimpleDialog } from "./_shared";
import { toast } from "../../components/ui/sonner";
import { useConfirm } from "../../components/ConfirmDialog";

export function CustomersTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");
  const [importInfo, setImportInfo] = useState("");
  const fileRef = useRef();
  const confirm = useConfirm();

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const name = (editing.name || "").trim();
    if (!name) return toast.error("Name required");
    const surcharge = Math.max(0, Number(editing.surcharge_amount || 0));
    try {
      const body = { name, notes: editing.notes || "", surcharge_amount: surcharge };
      if (editing.id) await api.put(`/customers/${editing.id}`, body);
      else await api.post("/customers", body);
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };
  const remove = async (id) => {
    const ok = await confirm({ title: "Delete this customer?", confirmText: "Delete", destructive: true });
    if (!ok) return;
    await api.delete(`/customers/${id}`);
    load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportInfo(`Imported · ${data.inserted} new, ${data.updated} updated${data.errors?.length ? `, ${data.errors.length} errors` : ""}`);
    } catch (err) {
      setImportInfo(err?.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
    load();
  };

  const downloadTemplate = () => {
    const csv = "name,notes,surcharge_amount\nACME Industrial Ltd,Long-term key account,100\nAce Schools,,0\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "customers-template.csv";
    a.click();
  };

  const downloadList = () => {
    const header = "name,notes,surcharge_amount\n";
    const esc = (s) => (String(s).includes(",") || String(s).includes('"') ? `"${String(s).replace(/"/g, '""')}"` : String(s));
    const rows = items.map((c) => `${esc(c.name)},${esc(c.notes || "")},${Number(c.surcharge_amount || 0)}`).join("\n");
    const blob = new Blob([header + rows + "\n"], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "customers.csv";
    a.click();
  };

  const visible = filter
    ? items.filter((c) => c.name.toLowerCase().includes(filter.toLowerCase()))
    : items;

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="text-xs text-slate-500 max-w-2xl">
        Customers appear as suggestions in the Quote Builder's Customer Name field once an estimator types 3 or more characters
        (prefix match — typing <code>ac</code> shows <em>ACME</em> but not <em>Pace</em>). Estimators can add a customer on the fly,
        and admins can manage the master list here.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setEditing({ name: "", notes: "" })} className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark" data-testid="new-customer-button">
          <Plus size={14} /> Add Customer
        </button>
        <button onClick={() => fileRef.current?.click()} className="h-9 px-4 border border-slate-900 hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="customers-import-button">
          <Upload size={14} /> Import CSV
        </button>
        <button onClick={downloadList} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2">
          <Download size={14} /> Download CSV
        </button>
        <button onClick={downloadTemplate} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="customers-template-button">
          <Download size={14} /> Template
        </button>
        <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" />
        <div className="flex-1" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter…"
          className="h-9 border border-slate-300 px-2 text-sm max-w-[200px]"
          data-testid="customers-filter-input"
        />
        {importInfo && <span className="text-xs text-slate-500 ml-2 basis-full">{importInfo}</span>}
      </div>
      <div className="border border-slate-200 max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50">
            <tr className="border-b-2 border-slate-900">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Name</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Notes</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600" title="Auto-added to every new quote for this customer">Surcharge (£)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 text-sm">No customers{filter ? " match" : " yet"}.</td></tr>}
            {visible.map((c) => (
              <tr key={c.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 text-slate-600">{c.notes || <span className="text-slate-400 italic">—</span>}</td>
                <td className={`px-3 py-2 text-right font-mono ${Number(c.surcharge_amount || 0) > 0 ? "text-brand-orange font-semibold" : "text-slate-300"}`}>
                  {Number(c.surcharge_amount || 0) > 0 ? `£${Number(c.surcharge_amount).toFixed(2)}` : "—"}
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button onClick={() => setEditing({ id: c.id, name: c.name, notes: c.notes || "", surcharge_amount: c.surcharge_amount || 0 })} className="text-slate-500 hover:text-slate-900"><Edit3 size={14} /></button>
                  <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <SimpleDialog title={editing.id ? "Edit Customer" : "New Customer"} onClose={() => setEditing(null)} onSave={save}>
          <Input label="Name" value={editing.name} set={(v) => setEditing({ ...editing, name: v })} />
          <Input label="Notes (optional)" value={editing.notes} set={(v) => setEditing({ ...editing, notes: v })} />
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Key-account surcharge (£)
            </span>
            <input
              type="number"
              step="0.01"
              min="0"
              className="qb-input"
              value={editing.surcharge_amount ?? 0}
              onChange={(e) => setEditing({ ...editing, surcharge_amount: e.target.value })}
              data-testid="customer-surcharge-input"
              placeholder="0.00"
            />
            <span className="block text-[11px] text-slate-500 mt-1">
              Flat £ amount auto-added to every new quote for this customer (e.g. admin fee). Estimators can still override per quote.
            </span>
          </label>
        </SimpleDialog>
      )}
    </div>
  );
}
